/**
 * src/three/RageInputDom.js
 * ช่องพิมพ์คำด่าเอง (รองรับไทย) — Tab เปิด/ปิด
 */
export default class RageInputDom {
  constructor({ onSubmit, onClose } = {}) {
    this.onSubmit = onSubmit || (() => {});
    this.onClose = onClose || (() => {});
    this.open = false;

    this.root = document.createElement('div');
    this.root.className = 'rage-input-root';
    this.root.hidden = true;
    this.root.innerHTML = `
      <label class="rage-input-label" for="rage-custom-input">พิมพ์กระสุนคำแล้วกดปุ่มเพิ่มคำ (รวมได้ 10 คำ)</label>
      <div class="rage-input-row">
        <input id="rage-custom-input" class="rage-input-field" type="text" maxlength="24"
          placeholder="เช่น ไอ้งาน... " autocomplete="off" spellcheck="false"
          inputmode="text" enterkeyhint="done" />
        <button type="button" class="rage-input-btn" data-action="send" aria-label="เพิ่มคำ">
          <span class="ui-label" data-label="เพิ่มคำ" aria-hidden="true"></span>
        </button>
        <button type="button" class="rage-input-btn rage-input-btn--cancel" data-action="close" aria-label="ปิด">
          <span class="ui-label" data-label="ปิด" aria-hidden="true"></span>
        </button>
      </div>
      <p class="rage-input-hint">Esc / Tab = ปิด</p>
    `;
    document.body.appendChild(this.root);

    this.input = this.root.querySelector('input');
    this._onOrient = () => this.onLayoutChange();
    this._chipsObserver = null;
    this._anchoredChips = null;
    this._layoutTimers = [];
    /** ขนาดจอตอนเปิดฟอร์ม — ไม่ตามคีย์บอร์ดที่ทำให้ความสูงหด */
    this._frozenW = 0;
    this._frozenH = 0;
    const preventSelect = (e) => e.preventDefault();
    const stop = (e) => e.stopPropagation();
    this.root.addEventListener('pointerdown', stop);
    this.root.addEventListener('pointerup', stop);
    this.root.addEventListener('mousedown', stop);
    this.root.addEventListener('touchstart', stop);
    this.root.addEventListener('touchend', stop);
    this.root.addEventListener('keydown', stop);
    this.root.addEventListener('keyup', stop);
    this.root.addEventListener('keypress', stop);
    this.root.querySelectorAll('button').forEach((btn) => {
      btn.addEventListener('contextmenu', preventSelect);
      btn.addEventListener('selectstart', preventSelect);
    });
    this.root.querySelector('[data-action="send"]').addEventListener('click', () => this.submit());
    this.root.querySelector('[data-action="close"]').addEventListener('click', () => this.hide());
    this.input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        this.submit();
      } else if (e.key === 'Escape' || e.key === 'Tab') {
        e.preventDefault();
        this.hide();
      }
    });
    // กันหน้าเลื่อนตามโฟกัส/คีย์บอร์ดบนมือถือ
    this.input.addEventListener('focus', () => {
      window.scrollTo(0, 0);
      this.syncScreenCenter();
    });
  }

  focusInput() {
    const el = this.input;
    if (!el) return;
    el.focus({ preventScroll: true });
    try {
      const n = el.value.length;
      el.setSelectionRange(n, n);
    } catch (_) { /* ignore */ }
  }

  isMobileUi() {
    return !!document.querySelector('.three-root.is-mobile');
  }

  /** ขนาดจอ layout — ไม่ใช้ visualViewport เพื่อไม่ให้ขยับตามคีย์บอร์ด */
  layoutSize() {
    const w = Math.round(document.documentElement.clientWidth || window.innerWidth || 0);
    const h = Math.round(document.documentElement.clientHeight || window.innerHeight || 0);
    return { w, h };
  }

  freezeLayoutSize() {
    const { w, h } = this.layoutSize();
    // ถ้าคีย์บอร์ดทำให้สูงหด ให้คงความสูงเดิมไว้
    if (this._frozenW && this._frozenH && w === this._frozenW && h < this._frozenH) {
      return;
    }
    this._frozenW = w;
    this._frozenH = h;
  }

  onLayoutChange() {
    if (!this.open || !this.isMobileUi()) return;
    const { w, h } = this.layoutSize();
    // ขยับเฉพาะหมุนจอ / เปลี่ยนความกว้าง — ไม่ตามคีย์บอร์ดที่หดความสูง
    const widthChanged = Math.abs(w - this._frozenW) > 2;
    const tallerAgain = h > this._frozenH + 2;
    if (!widthChanged && !tallerAgain) return;
    this._frozenW = w;
    this._frozenH = Math.max(h, this._frozenH);
    if (widthChanged) this._frozenH = h;
    this.syncScreenCenter();
  }

  bindOrient() {
    window.addEventListener('orientationchange', this._onOrient);
    window.addEventListener('resize', this._onOrient);
  }

  unbindOrient() {
    window.removeEventListener('orientationchange', this._onOrient);
    window.removeEventListener('resize', this._onOrient);
  }

  chipsEl() {
    return document.querySelector('.three-root.is-mobile .bullet-picks--typing')
      || document.querySelector('.three-root.is-mobile .bullet-picks');
  }

  setTypingClass(on) {
    document.body.classList.toggle('is-rage-typing', !!on);
  }

  observeChips() {
    const chips = this.chipsEl();
    if (chips === this._anchoredChips && this._chipsObserver) return;
    this.unobserveChips();
    if (!chips || typeof ResizeObserver === 'undefined') return;
    this._anchoredChips = chips;
    this._chipsObserver = new ResizeObserver(() => this.syncScreenCenter());
    this._chipsObserver.observe(chips);
  }

  unobserveChips() {
    this._chipsObserver?.disconnect();
    this._chipsObserver = null;
    this._anchoredChips = null;
  }

  clearFormPosition() {
    if (!this.root) return;
    this.root.style.top = '';
    this.root.style.left = '';
    this.root.style.bottom = '';
    this.root.style.right = '';
    this.root.style.transform = '';
  }

  clearChipsPosition() {
    const el = this._anchoredChips
      || document.querySelector('.bullet-picks.is-form-anchored')
      || document.querySelector('.three-root.is-mobile .bullet-picks--typing');
    if (!el) return;
    el.classList.remove('is-form-anchored');
    el.style.top = '';
    el.style.left = '';
    el.style.bottom = '';
    el.style.right = '';
    el.style.transform = '';
  }

  clearLayoutTimers() {
    this._layoutTimers.forEach((id) => window.clearTimeout(id));
    this._layoutTimers = [];
  }

  /** จัดกลุ่มชิป + ฟอร์มกลางจอ — ใช้ขนาดจอที่ล็อกไว้ ไม่ตามคีย์บอร์ด */
  syncScreenCenter() {
    if (!this.root || !this.open || !this.isMobileUi()) {
      this.clearFormPosition();
      this.clearChipsPosition();
      return;
    }

    if (!this._frozenW || !this._frozenH) this.freezeLayoutSize();
    const viewW = this._frozenW;
    const viewH = this._frozenH;
    const cx = viewW / 2;
    const gap = 10;
    const pad = 10;

    const chips = this.chipsEl();
    if (chips) chips.classList.add('is-form-anchored');

    const formH = this.root.offsetHeight;
    const chipsH = chips ? chips.offsetHeight : 0;
    const groupH = formH + (chipsH ? chipsH + gap : 0);
    const minTop = pad;
    const maxTop = Math.max(viewH - groupH - pad, pad);
    const groupTop = Math.min(Math.max((viewH - groupH) / 2, minTop), maxTop);

    if (chips && chipsH) {
      chips.style.top = `${Math.round(groupTop)}px`;
      chips.style.left = `${Math.round(cx)}px`;
      chips.style.bottom = 'auto';
      chips.style.right = 'auto';
      chips.style.transform = 'translateX(-50%)';
    }

    this.root.style.top = `${Math.round(groupTop + (chipsH ? chipsH + gap : 0))}px`;
    this.root.style.left = `${Math.round(cx)}px`;
    this.root.style.bottom = 'auto';
    this.root.style.right = 'auto';
    this.root.style.transform = 'translateX(-50%)';
  }

  layoutAfterPaint() {
    this.clearLayoutTimers();
    const hook = () => {
      if (!this.open) return;
      this.observeChips();
      this.syncScreenCenter();
      this.focusInput();
    };
    requestAnimationFrame(() => {
      hook();
      requestAnimationFrame(hook);
    });
    // จัด layout หลังเปิดฟอร์มเท่านั้น — ไม่รอจังหวะคีย์บอร์ด
    [40, 120].forEach((ms) => {
      this._layoutTimers.push(window.setTimeout(hook, ms));
    });
  }

  show() {
    this.open = true;
    this.setTypingClass(true);
    this.root.hidden = false;
    this.input.value = '';
    this._frozenW = 0;
    this._frozenH = 0;
    this.freezeLayoutSize();
    this.bindOrient();
    this.syncScreenCenter();
    this.focusInput();
    this.layoutAfterPaint();
  }

  hide() {
    const was = this.open;
    this.open = false;
    this.setTypingClass(false);
    this.root.hidden = true;
    this.clearLayoutTimers();
    this.unbindOrient();
    this.clearChipsPosition();
    this.unobserveChips();
    this.clearFormPosition();
    this._frozenW = 0;
    this._frozenH = 0;
    this.input.blur();
    if (was) this.onClose();
  }

  toggle() {
    if (this.open) this.hide();
    else this.show();
  }

  submit() {
    const text = this.input.value.trim();
    if (text) this.onSubmit(text);
    this.input.value = '';
    if (this.isMobileUi() && this.open) {
      this.layoutAfterPaint();
      return;
    }
    this.hide();
  }

  dispose() {
    this.clearLayoutTimers();
    this.setTypingClass(false);
    this.unbindOrient();
    this.unobserveChips();
    this.root?.remove();
    this.root = null;
  }
}
